import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import ThreeCirclesModal, { EMPTY_THREE_CIRCLES, ThreeCirclesData } from '../components/ThreeCirclesModal';
import StepShareDialog from '../components/StepShareDialog';
import { HTMLService } from '../services/htmlService';
import { progressService } from '../services/progressService';
import { SponsorService, Sponsor } from '../services/sponsorService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { useLocale } from '../context/LocaleContext';
import { useProfileModal } from '../context/ProfileModalContext';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import styles from '../styles/stepCommon.module.css';
import circleStyles from '../styles/threeCirclesModal.module.css';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepHelpButton from '../components/StepHelpButton';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getGettingStartedCongratsHeading,
  getNextStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
} from '../config/stepPageCopy';

export type SponsorStatus = 'have_sponsor' | 'looking' | 'ask_in_meeting';

interface GettingStartedProps {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const READINGS_FILE = '/content/010_GETTING_STARTED_READINGS.md';

const GettingStarted: React.FC<GettingStartedProps> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { t } = useLocale();
  const { openProfileModal, isProfileModalOpen } = useProfileModal();
  const [isAddictionReaderOpen, setIsAddictionReaderOpen] = useState(false);
  const [isFellowshipReaderOpen, setIsFellowshipReaderOpen] = useState(false);
  const [is3CirclesReaderOpen, setIs3CirclesReaderOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isCirclesModalOpen, setIsCirclesModalOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [threeCircles, setThreeCircles] = useState<ThreeCirclesData>(EMPTY_THREE_CIRCLES);
  const [sponsorStatus, setSponsorStatus] = useState<SponsorStatus | ''>('');
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const prevProfileModalOpen = useRef(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(0, 1),
      description: getStepActionDescription(0, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsAddictionReaderOpen(true),
    },
    {
      id: 2,
      title: getStepActionLabel(0, 2),
      description: getStepActionDescription(0, 2),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsFellowshipReaderOpen(true),
    },
    {
      id: 3,
      title: getStepActionLabel(0, 3),
      description: getStepActionDescription(0, 3),
      completed: false,
    },
    {
      id: 4,
      title: getStepActionLabel(0, 4),
      description: getStepActionDescription(0, 4),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIs3CirclesReaderOpen(true),
    },
    {
      id: 5,
      title: getStepActionLabel(0, 5),
      description: getStepActionDescription(0, 5),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.WATCH_VIDEO,
      buttonAction: () => setIsVideoModalOpen(true),
    },
    {
      id: 6,
      title: getStepActionLabel(0, 6),
      description: getStepActionDescription(0, 6),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.ENTER_UPDATE_3_CIRCLES,
      buttonAction: () => setIsCirclesModalOpen(true),
    },
    {
      id: 7,
      title: getStepActionLabel(0, 7),
      description: getStepActionDescription(0, 7),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.SHARE_WITH_SPONSOR,
      buttonAction: () => setIsShareDialogOpen(true),
    },
    {
      id: 8,
      title: getStepActionLabel(0, 8),
      description: getStepActionDescription(0, 8),
      completed: false,
    },
  ];

  useRelocalizeStepActions(originalActions, setActions);

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(0);

  const mergeActions = (saved: { actions?: unknown; action_schema_version?: number }, stepCompleted: boolean) =>
    mergeStepActions(0, originalActions, saved, stepCompleted);

  const saveProgress = async (
    updatedActions: ActionItem[],
    extraData?: { three_circles?: ThreeCirclesData; sponsor_status?: SponsorStatus | '' }
  ) => {
    const allCompleted = updatedActions.every((a) => a.completed);
    const schemaVersion = getStepActionSchema(0).actionSchemaVersion;
    await progressService.updateStepProgress({
      program: 'SAA',
      track: '12-steps',
      step_number: 0,
      step_title: 'Getting Started',
      completed: allCompleted,
      data: stepDataWithActions(updatedActions, schemaVersion, {
        three_circles: extraData?.three_circles ?? threeCircles,
        sponsor_status: extraData?.sponsor_status ?? sponsorStatus,
      }),
    });
  };

  const loadSponsors = async () => {
    if (!user) {
      setSponsors([]);
      return;
    }
    try {
      const list = await SponsorService.getSponsors();
      setSponsors(list);
    } catch {
      setSponsors([]);
    }
  };

  const loadPageData = async () => {
    if (!user) {
      setActions(originalActions);
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);
    try {
      perfMark('GettingStarted', 'loadPageData start');
      const [existing, list] = await perfTimeAsync('GettingStarted loadPageData (total)', () =>
        Promise.all([
          progressService.loadStepPageProgress('SAA', '12-steps', 0),
          perfTimeAsync('GET /sponsors', () => SponsorService.getSponsors().catch(() => [] as Sponsor[])),
        ])
      );
      setSponsors(list);

      if (existing?.data) {
        const saved = existing.data;
        if (saved.actions) {
          const merged = mergeActions(saved, !!existing.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 0,
              step_title: 'Getting Started',
              completed: !!existing.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, saved),
            });
          }
        } else {
          setActions(originalActions);
        }
        if (saved.three_circles) {
          setThreeCircles({
            inner: saved.three_circles.inner || [],
            middle: saved.three_circles.middle || [],
            outer: saved.three_circles.outer || [],
          });
        }
        if (saved.sponsor_status) {
          setSponsorStatus(saved.sponsor_status);
        }
      } else {
        setActions(originalActions);
      }
      clearError();
      perfSummary('Getting Started page');
    } catch (err: any) {
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, [user, stepWorkspaceRevision]);

  useEffect(() => {
    if (prevProfileModalOpen.current && !isProfileModalOpen && user) {
      loadSponsors();
    }
    prevProfileModalOpen.current = isProfileModalOpen;
  }, [isProfileModalOpen, user]);

  const circlesComplete = (circles: ThreeCirclesData) =>
    circles.inner.length >= 1 && circles.middle.length >= 1 && circles.outer.length >= 1;

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }
    try {
      setActions(updatedActions);
      await saveProgress(updatedActions);
    } catch (err: any) {
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  const handleSponsorStatusChange = async (status: SponsorStatus) => {
    setSponsorStatus(status);
    if (!user) return;
    try {
      await saveProgress(actions, { sponsor_status: status });
    } catch (err: any) {
      handleError(err, t('step.page.error.save_sponsor'));
    }
  };

  const handleThreeCirclesSave = useCallback(
    async (circles: ThreeCirclesData) => {
      setThreeCircles(circles);
      if (!user) return;

      let updatedActions = actions;
      const action6 = actions.find((a) => a.id === 6);
      if (action6?.completed && !circlesComplete(circles)) {
        updatedActions = actions.map((a) => (a.id === 6 ? { ...a, completed: false } : a));
        setActions(updatedActions);
      }

      await saveProgress(updatedActions, { three_circles: circles });
    },
    [user, actions, threeCircles, sponsorStatus]
  );

  const canShareGettingStarted = () => {
    const hasCompletedWork = actions.some((a) => a.completed && a.id !== 7 && a.id !== 8);
    const hasCircles =
      threeCircles.inner.length > 0 ||
      threeCircles.middle.length > 0 ||
      threeCircles.outer.length > 0;
    return hasCompletedWork || hasCircles;
  };

  const buildGettingStartedShareHtml = () =>
    HTMLService.generateGettingStartedSummaryHTML(
      { name: user?.name || user?.display_name, email: user?.email },
      actions.map(({ id, completed }) => ({ id, completed })),
      threeCircles,
      sponsorStatus || undefined
    );

  const canCompleteAction = (actionId: number) => {
    if (actionId === 6) return circlesComplete(threeCircles);
    return true;
  };

  const saaSponsors = sponsors.filter((s) => s.program === 'SAA');

  const renderCirclesPreview = () => {
    const hasAny =
      threeCircles.inner.length > 0 ||
      threeCircles.middle.length > 0 ||
      threeCircles.outer.length > 0;
    if (!hasAny) return null;

    const sections = [
      { key: 'inner' as const, label: t('getting_started.circles.preview.inner') },
      { key: 'middle' as const, label: t('getting_started.circles.preview.middle') },
      { key: 'outer' as const, label: t('getting_started.circles.preview.outer') },
    ];

    return (
      <div className={styles.actionContentIndent}>
        <div className={circleStyles.circlesPreview}>
          {sections.map(({ key, label }) =>
            threeCircles[key].length > 0 ? (
              <div key={key} className={circleStyles.circlesPreviewSection}>
                <p className={circleStyles.circlesPreviewHeading}>{label}</p>
                <ul className={circleStyles.circlesPreviewList}>
                  {threeCircles[key].map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </div>
      </div>
    );
  };

  const renderSponsorContent = () => (
    <div className={styles.actionContentIndent}>
      <div className={circleStyles.sponsorOptions}>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'have_sponsor'}
            onChange={() => handleSponsorStatusChange('have_sponsor')}
          />
          {t('getting_started.sponsor.have_sponsor')}
        </label>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'looking'}
            onChange={() => handleSponsorStatusChange('looking')}
          />
          {t('getting_started.sponsor.looking')}
        </label>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'ask_in_meeting'}
            onChange={() => handleSponsorStatusChange('ask_in_meeting')}
          />
          {t('getting_started.sponsor.ask_in_meeting')}
        </label>
      </div>
      <button
        type="button"
        className={styles.actionButton}
        onClick={() => openProfileModal('sponsorship')}
      >
        {t('getting_started.sponsor.open_profile_cta')}
      </button>
      {saaSponsors.length > 0 && (
        <div className={circleStyles.sponsorInfo}>
          <p>
            {saaSponsors.length > 1
              ? t('getting_started.sponsor.added_many')
              : t('getting_started.sponsor.added_one')}
          </p>
        </div>
      )}
    </div>
  );

  const allCompleted = actions.every((a) => a.completed);

  return (
    <div className={styles.stepPage}>
      <Header breadcrumbs={breadcrumbs} user={user} onLogin={onLogin} onLogout={onLogout} />

      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(0)}</h1>
            <StepHelpButton stepKey="app" />
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(0)}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && <StepLoginPrompt onLogin={onLogin} />}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(0)}</h2>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>

            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              canCompleteAction={canCompleteAction}
              renderActionContent={(action) => {
                if (action.id === 3) {
                  return renderSponsorContent();
                }
                if (action.id === 6) {
                  return renderCirclesPreview();
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getGettingStartedCongratsHeading()}</h3>
                  <p>{getCongratsBody(0)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <span />
                    <Link to="/saa/12-steps/step-1" className={styles.nextStepLink}>
                      {getNextStepNavLabel(0)}
                    </Link>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <span />
                  <Link to="/saa/12-steps/step-1" className={styles.nextStepLink}>
                    {getNextStepNavLabel(0)}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <ContentReader
        isOpen={isAddictionReaderOpen}
        onClose={() => setIsAddictionReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-addiction start>>"
        endTag="<<getting-started-addiction end>>"
      />
      <ContentReader
        isOpen={isFellowshipReaderOpen}
        onClose={() => setIsFellowshipReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-fellowship start>>"
        endTag="<<getting-started-fellowship end>>"
      />
      <ContentReader
        isOpen={is3CirclesReaderOpen}
        onClose={() => setIs3CirclesReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-3circles-reading start>>"
        endTag="<<getting-started-3circles-reading end>>"
      />
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="VqWwh_rUZuY"
        title={t('getting_started.video.title')}
      />
      <ThreeCirclesModal
        isOpen={isCirclesModalOpen}
        onClose={() => setIsCirclesModalOpen(false)}
        user={user}
        circles={threeCircles}
        onSave={handleThreeCirclesSave}
      />
      <StepShareDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        user={user}
        stepTitle={getStepPageTitle(0)}
        shareName="Getting Started Summary"
        documentType="getting-started"
        generateHtml={buildGettingStartedShareHtml}
        canGenerate={canShareGettingStarted}
        emptyMessage={t('getting_started.share.empty_message')}
      />
    </div>
  );
};

export default GettingStarted;
